import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button } from "antd";
import { EvaSource } from "constants/charts.enums";
import { selectAllCharts, setDataSourceSetting, updateChartByPath } from "store/features";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import useFeatureFlags from "hooks/useFeatureFlags";

import { DataSourceIcon, DataSourceSelect } from "components/shared";

const GlobalDataSourceSelect = () => {
  const dispatch = useDispatch();
  const { hasFeature } = useFeatureFlags();
  const syncState = useSelector((state: RootState) => state.app.syncWells);
  const allCharts = useSelector(selectAllCharts);
  // state from store
  const dataSourceSetting = useSelector(
    (state: RootState) => state.app.pdenDataSourceSetting
  );

  // derived state
  const isHybrid = dataSourceSetting.source === "Hybrid";

  // set to hybrid when sync is on
  useEffect(() => {
    if (!syncState?.isSync || isHybrid || syncState?.syncRequestOrigin !== "Focus") {
      return;
    }

    const nextSourceSetting = {
      ...dataSourceSetting,
      source: EvaSource.Hybrid
    };
    dispatch(setDataSourceSetting(nextSourceSetting));

    // updating all new charts
    Object.keys(allCharts).forEach((chartId) => {
      dispatch(
        updateChartByPath({
          id: chartId,
          path: "features.source.properties.value",
          value: EvaSource.Hybrid
        })
      );
    });
  }, [syncState]);

  function handleSourceChange(value: EvaSource): void {
    const nextSourceSetting = {
      ...dataSourceSetting,
      source: value
    };
    dispatch(setDataSourceSetting(nextSourceSetting));
    Object.keys(allCharts).forEach((chartId) => {
      const chartSource = allCharts[chartId]?.features?.source?.properties?.value;
      // different sources means there is a chart specific source and we shouldn't modify
      if (chartSource === dataSourceSetting.source) {
        dispatch(
          updateChartByPath({
            id: chartId,
            path: "features.source.properties.value",
            value: value
          })
        );
      }
    });
  }

  // EVA-334: hide only — the persisted source stays in effect, and the
  // ClientSync Hybrid effect above must keep running.
  if (!hasFeature("Data Source Toggles")) return null;

  return (
    <DataSourceSelect
      trigger={["click"]}
      value={dataSourceSetting.source}
      onChange={handleSourceChange}>
      <StyledButton icon={DataSourceIcon[dataSourceSetting.source]} />
    </DataSourceSelect>
  );
};

const StyledButton = styled(Button)`
  background: transparent;
  color: #a2aaad;
  &:hover {
    color: var(--color-text);
  }
`;

export default GlobalDataSourceSelect;
